<template>
  <LeadConfigSidebar
    v-model:isVisible="configSidebarVisible"
    @onSave="handleConfigSave"
    :saving="savingConfig"
  />

  <div class="page-container">
    <div class="max-w-7xl mx-auto p-6">
      <!-- Header Section -->
      <div class="bg-white border-round-2xl shadow-1 p-6 mb-6">
        <div class="flex align-items-center justify-content-between">
          <div class="flex align-items-center gap-4">
            <div class="header-icon-container">
              <i class="fa fa-users text-white text-2xl"></i>
            </div>
            <div>
              <div class="flex align-items-center gap-3 mb-2">
                <h1 class="font-bold text-gray-800 text-3xl m-0">Lead Management</h1>
                <div class="lead-status-banner-title" v-if="!loadingConfig">
                  <div :class="leadConfigEnabled ? 'status-enabled-title' : 'status-disabled-title'" class="status-badge-title">
                    <i :class="leadConfigEnabled ? 'fa fa-check-circle' : 'fa fa-times-circle'" class="status-icon-title"></i>
                    <span class="status-text-title">{{ leadConfigEnabled ? 'Popup Enabled' : 'Popup Disabled' }}</span>
                  </div>
                </div>
              </div>
              <p class="text-gray-600 m-0 text-lg">Manage and track potential customer leads</p>
            </div>
          </div>
          <div class="flex align-items-center gap-3">
            <!-- Configuration Button -->
            <Button
              @click="configSidebarVisible = !configSidebarVisible"
              label="Configuration"
              icon="fa fa-cog"
              class="config-button"
              outlined
            />
            <!-- Auto-refresh status -->
            <div class="auto-refresh-status">
              <small class="text-gray-500">
                <i class="fa fa-sync-alt fa-spin mr-1"></i>
                Auto-refreshing
              </small>
            </div>
          </div>
        </div>
      </div>

      <!-- Filters Section -->
      <div class="bg-white border-round-2xl shadow-1 p-6 mb-6">
        <div class="flex align-items-center gap-3 mb-4">
          <div class="bg-blue-100 p-2 border-round-lg">
            <i class="fa fa-filter text-blue-600"></i>
          </div>
          <h3 class="text-lg font-medium text-gray-700 m-0">Filter Leads</h3>
        </div>
        
        <div class="filter-row">
          <div class="filter-item">
            <label class="filter-label">Date Range:</label>
            <Dropdown 
              v-model="filters.timespan" 
              :options="timespanOptions" 
              optionLabel="label"
              optionValue="value"
              placeholder="Select Date Range"
              @change="loadLeads"
              class="filter-input"
            />
          </div>
          <div class="filter-item">
            <label class="filter-label">Status:</label>
            <Dropdown 
              v-model="filters.status" 
              :options="statusOptions" 
              optionLabel="label"
              optionValue="value"
              placeholder="All Statuses"
              @change="loadLeads"
              class="filter-input"
            />
          </div>
        </div>
      </div>

      <!-- Leads Table -->
      <div class="bg-white border-round-2xl shadow-1 overflow-hidden">
        <!-- Table Header -->
        <div class="table-header-section">
          <div class="flex align-items-center gap-3">
            <div class="bg-green-100 p-2 border-round-lg">
              <i class="fa fa-list text-green-600"></i>
            </div>
            <h2 class="text-xl font-semibold text-gray-800 m-0">Leads Overview ({{ leads.length }})</h2>
            <div class="ml-auto">
              <small class="text-gray-500">
                <i class="fa fa-info-circle mr-1"></i>
                Click on actions to manage leads
              </small>
            </div>
          </div>
        </div>

        <Loading
          v-if="loading"
          :full="false"
          title="Loading leads..."
        />

        <DataTable 
          v-else
          :value="leads" 
          :loading="loading"
          paginator 
          :rows="20"
          :rowsPerPageOptions="[10, 20, 50]"
          responsiveLayout="scroll"
          class="modern-datatable"
        >
          <Column field="created" header="Date" sortable>
            <template #body="{ data }">
              <div class="date-cell">
                <i class="fa fa-calendar text-blue-500 mr-2"></i>
                {{ formatDate(data.created) }}
              </div>
            </template>
          </Column>
          
          <Column field="city" header="City" sortable>
            <template #body="{ data }">
              <div class="city-cell">
                <i class="fa fa-map-marker text-green-500 mr-2"></i>
                {{ data.city }}
              </div>
            </template>
          </Column>
          
          <Column field="date" header="Planned Date" sortable>
            <template #body="{ data }">
              <div class="date-cell">
                <i class="fa fa-calendar-check text-purple-500 mr-2"></i>
                {{ formatPlannedDate(data.date) }}
              </div>
            </template>
          </Column>
          
          <Column field="numberOfGuests" header="Guests" sortable>
            <template #body="{ data }">
              <div class="guests-cell">
                <div class="guests-badge">
                  <i class="fa fa-users text-sm"></i>
                  <span>{{ data.numberOfGuests }}</span>
                </div>
              </div>
            </template>
          </Column>
          
          <Column field="desiredLocation" header="Desired Location">
            <template #body="{ data }">
              <div class="location-cell">
                <i class="fa fa-map text-orange-500 mr-2"></i>
                {{ data.desiredVenueName || 'Not specified' }}
              </div>
            </template>
          </Column>
          
          <Column field="contactPhoneNumber" header="Phone">
            <template #body="{ data }">
              <div class="phone-cell">
                <i class="fa fa-phone text-teal-500 mr-2"></i>
                {{ data.contactPhoneNumber }}
              </div>
            </template>
          </Column>
          
          <Column field="status" header="Status" sortable>
            <template #body="{ data }">
              <div class="status-cell">
                <div :class="getStatusClass(data.status)" class="status-badge">
                  <i :class="getStatusIcon(data.status)" class="text-sm"></i>
                  <span>{{ data.status }}</span>
                </div>
              </div>
            </template>
          </Column>
          
          <Column header="Actions" width="200">
            <template #body="{ data }">
              <div class="action-buttons">
                <Button 
                  icon="pi pi-pencil" 
                  size="small"
                  @click="editLead(data)"
                  text
                  class="action-button edit-button"
                  v-tooltip.top="'Edit Lead'"
                />
                <Button 
                  icon="pi pi-eye" 
                  size="small"
                  @click="viewLead(data)"
                  text
                  class="action-button view-button"
                  v-tooltip.top="'View Details'"
                />
              </div>
            </template>
          </Column>
        </DataTable>

        <!-- Empty State -->
        <div v-if="leads.length === 0 && !loading" class="text-center py-8">
          <i class="fa fa-inbox text-gray-400 text-4xl mb-3"></i>
          <p class="text-gray-500 text-lg m-0">No leads found</p>
          <p class="text-gray-400 text-sm m-0 mt-2">Try adjusting your filters or check back later</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Edit Lead Dialog -->
  <Dialog 
    v-model:visible="editDialogVisible" 
    header="Edit Lead" 
    modal
    :style="{ width: '500px' }"
    class="modern-dialog"
  >
    <div class="edit-form">
      <div class="form-group">
        <label class="form-label">Status:</label>
        <Dropdown 
          v-model="editingLead.status" 
          :options="statusOptions.filter(opt => opt.value !== null)" 
          optionLabel="label"
          optionValue="value"
          class="form-input"
        />
      </div>
      
      <div class="form-group">
        <label class="form-label">Note:</label>
        <Textarea 
          v-model="editingLead.note" 
          rows="4"
          placeholder="Add a note about this lead..."
          class="form-input"
        />
      </div>
    </div>
    
    <template #footer>
      <Button label="Cancel" @click="editDialogVisible = false" text class="cancel-button" />
      <Button label="Save" @click="saveLead" :loading="saving" class="save-button" />
    </template>
  </Dialog>

  <!-- View Lead Dialog -->
  <Dialog 
    v-model:visible="viewDialogVisible" 
    header="Lead Details" 
    modal
    :style="{ width: '600px' }"
    class="modern-dialog"
  >
    <div class="lead-details" v-if="viewingLead">
      <div class="detail-row">
        <label class="detail-label">Date Submitted:</label>
        <span class="detail-value">{{ formatDate(viewingLead.created) }}</span>
      </div>
      <div class="detail-row">
        <label class="detail-label">City:</label>
        <span class="detail-value">{{ viewingLead.city }}</span>
      </div>
      <div class="detail-row">
        <label class="detail-label">Planned Date:</label>
        <span class="detail-value">{{ formatPlannedDate(viewingLead.date) }}</span>
      </div>
      <div class="detail-row">
        <label class="detail-label">Number of Guests:</label>
        <span class="detail-value">{{ viewingLead.numberOfGuests }}</span>
      </div>
      <div class="detail-row">
        <label class="detail-label">Desired Location:</label>
        <span class="detail-value">{{ viewingLead.desiredVenueName || 'Not specified' }}</span>
      </div>
      <div class="detail-row">
        <label class="detail-label">Contact Phone:</label>
        <span class="detail-value">{{ viewingLead.contactPhoneNumber }}</span>
      </div>
      <div class="detail-row">
        <label class="detail-label">First Name:</label>
        <span class="detail-value">{{ viewingLead.firstName || 'Not provided' }}</span>
      </div>
      <div class="detail-row">
        <label class="detail-label">Status:</label>
        <div :class="getStatusClass(viewingLead.status)" class="status-badge">
          <i :class="getStatusIcon(viewingLead.status)" class="text-sm"></i>
          <span>{{ viewingLead.status }}</span>
        </div>
      </div>
      <div class="detail-row" v-if="viewingLead.note">
        <label class="detail-label">Note:</label>
        <span class="detail-value">{{ viewingLead.note }}</span>
      </div>
    </div>
    
    <template #footer>
      <Button label="Close" @click="viewDialogVisible = false" class="close-button" />
    </template>
  </Dialog>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { leadsApi } from '@/api/leads'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Dropdown from 'primevue/dropdown'
import Textarea from 'primevue/textarea'
import Loading from '@/components/Loading.vue'
import LeadConfigSidebar from './components/LeadConfigSidebar.vue'
import dayjs from 'dayjs'

export default {
  name: 'LeadsPage',
  components: {
    Button,
    Dialog,
    DataTable,
    Column,
    Dropdown,
    Textarea,
    Loading,
    LeadConfigSidebar
  },
  setup() {
    const leads = ref([])
    const loading = ref(false)
    const saving = ref(false)
    const editDialogVisible = ref(false)
    const viewDialogVisible = ref(false)
    const editingLead = ref({})
    const viewingLead = ref(null)
    const refreshInterval = ref(null)
    const configSidebarVisible = ref(false)
    const savingConfig = ref(false)
    const leadConfigEnabled = ref(false)
    const loadingConfig = ref(false)
    
    const filters = ref({
      timespan: '3days',
      status: null
    })
    
    const statusOptions = [
      { label: 'All Statuses', value: null },
      { label: 'New', value: 'New' },
      { label: 'In Progress', value: 'InProgress' },
      { label: 'Completed', value: 'Completed' }
    ]

    const timespanOptions = [
      // { label: 'Last Hour', value: '1hour' },
      { label: 'Today', value: 'today' },
      { label: '3 Days', value: '3days' },
      { label: '1 Week', value: '1week' },
      { label: '1 Month', value: '1month' }
    ]

    const getDateRangeFromTimespan = (timespan) => {
      const now = new Date()
      const fromDate = new Date()
      
      switch (timespan) {
        // case '1hour':
        //   fromDate.setHours(now.getHours() - 1)
        //   break
        case 'today':
          fromDate.setHours(0, 0, 0, 0)
          break
        case '3days':
          fromDate.setDate(now.getDate() - 3)
          break
        case '1week':
          fromDate.setDate(now.getDate() - 7)
          break
        case '1month':
          fromDate.setMonth(now.getMonth() - 1)
          break
        default:
          fromDate.setDate(now.getDate() - 3) // Default to 3 days
      }
      
      return { fromDate, toDate: now }
    }

    const formatDateForApi = (date) => {
      // Format date as YYYY-MM-DDTHH:mm:ss to avoid timezone issues
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
    }

    const loadLeads = async (showLoading = true) => {
      if (showLoading) {
        loading.value = true
      }
      try {
        const { fromDate, toDate } = getDateRangeFromTimespan(filters.value.timespan)
        const params = {
          fromDate: formatDateForApi(fromDate),
          toDate: formatDateForApi(toDate)
        }
        
        if (filters.value.status) {
          params.status = filters.value.status
        }
        
        const response = await leadsApi.getLeads(params)
        const previousCount = leads.value.length
        leads.value = response.data
        
        // Show subtle feedback for auto-refresh when new leads are found
        if (!showLoading && leads.value.length > previousCount) {
          console.log(`Auto-refresh: Found ${leads.value.length - previousCount} new leads`)
        }
      } catch (error) {
        console.error('Error loading leads:', error)
      } finally {
        if (showLoading) {
          loading.value = false
        }
      }
    }

    const startAutoRefresh = () => {
      if (refreshInterval.value) {
        clearInterval(refreshInterval.value)
      }
      
      refreshInterval.value = setInterval(() => {
        loadLeads(false) // Don't show loading indicator for auto-refresh
      }, 1500) // 1 seconds
    }

    const stopAutoRefresh = () => {
      if (refreshInterval.value) {
        clearInterval(refreshInterval.value)
        refreshInterval.value = null
      }
    }

    const editLead = (lead) => {
      editingLead.value = { ...lead }
      editDialogVisible.value = true
    }

    const viewLead = (lead) => {
      viewingLead.value = lead
      viewDialogVisible.value = true
    }

    const saveLead = async () => {
      saving.value = true
      try {
        await leadsApi.updateLead(editingLead.value.id, {
          status: editingLead.value.status,
          note: editingLead.value.note
        })
        
        // Update the lead in the list
        const index = leads.value.findIndex(l => l.id === editingLead.value.id)
        if (index !== -1) {
          leads.value[index] = { ...leads.value[index], ...editingLead.value }
        }
        
        editDialogVisible.value = false
      } catch (error) {
        console.error('Error saving lead:', error)
      } finally {
        saving.value = false
      }
    }

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString()
    }

    const formatPlannedDate = (dateString) => {
      const date = dayjs(dateString);
      
      if (date.year() === 1 || date.year() === 9999)
        return '-'
      
      return date.format('dddd DD/MM/YYYY');
    }

    const getStatusClass = (status) => {
      switch (status) {
        case 'New': return 'status-new'
        case 'InProgress': return 'status-progress'
        case 'Completed': return 'status-completed'
        default: return 'status-new'
      }
    }

    const getStatusIcon = (status) => {
      switch (status) {
        case 'New': return 'fa fa-plus-circle'
        case 'InProgress': return 'fa fa-clock'
        case 'Completed': return 'fa fa-check-circle'
        default: return 'fa fa-plus-circle'
      }
    }

    const loadLeadConfiguration = async () => {
      loadingConfig.value = true
      try {
        const response = await leadsApi.getLeadConfiguration()
        if (response.data) {
          leadConfigEnabled.value = response.data.leadGenerationPopupEnabled || false
        }
      } catch (error) {
        console.error('Error loading lead configuration:', error)
        leadConfigEnabled.value = false
      } finally {
        loadingConfig.value = false
      }
    }

    const handleConfigSave = (configData) => {
      console.log('Configuration saved:', configData)
      // Update the local state when configuration is saved
      if (configData && typeof configData.leadGenerationPopupEnabled !== 'undefined') {
        leadConfigEnabled.value = configData.leadGenerationPopupEnabled
      }
    }

    onMounted(() => {
      loadLeads()
      loadLeadConfiguration()
      startAutoRefresh()
    })

    onUnmounted(() => {
      stopAutoRefresh()
    })

    return {
      leads,
      loading,
      saving,
      editDialogVisible,
      viewDialogVisible,
      editingLead,
      viewingLead,
      filters,
      statusOptions,
      timespanOptions,
      editLead,
      viewLead,
      saveLead,
      formatDate,
      formatPlannedDate,
      getStatusClass,
      getStatusIcon,
      configSidebarVisible,
      savingConfig,
      handleConfigSave,
      leadConfigEnabled,
      loadingConfig
    }
  }
}
</script>

<style scoped>
/* Fixed background that doesn't scroll */
.page-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e0f2fe 100%);
  background-attachment: fixed;
}

/* Header icon container */
.header-icon-container {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  padding: 0.75rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Configuration button styling */
.config-button {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%) !important;
  color: white !important;
  border: none !important;
  padding: 0.75rem 1.5rem !important;
  border-radius: 0.75rem !important;
  font-weight: 600 !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
  transition: all 0.3s ease !important;
}

.config-button:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
  transform: translateY(-1px) !important;
}

/* Lead status banner in title styling */
.lead-status-banner-title {
  display: flex;
  align-items: center;
}

.status-badge-title {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.status-enabled-title {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.status-disabled-title {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.status-icon-title {
  font-size: 0.875rem;
}

.status-text-title {
  font-weight: 700;
  letter-spacing: 0.05em;
}

/* Auto-refresh status styling */
.auto-refresh-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Table header section */
.table-header-section {
  background: #f8fafc;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* Filter section styling */
.filter-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  align-items: end;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-label {
  font-weight: 600;
  font-size: 0.875rem;
  color: #374151;
}

.filter-input {
  width: 100%;
}

/* Modern datatable styling */
:deep(.modern-datatable) {
  border: none;
  border-radius: 0;
}

:deep(.modern-datatable .p-datatable-thead > tr > th) {
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 600;
  color: #374151;
  padding: 1rem 1.5rem;
}

:deep(.modern-datatable .p-datatable-tbody > tr) {
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.2s ease;
}

:deep(.modern-datatable .p-datatable-tbody > tr:hover) {
  background: #f8fafc;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

:deep(.modern-datatable .p-datatable-tbody > tr > td) {
  padding: 1rem 1.5rem;
  border: none;
}

/* Cell styling */
.date-cell,
.city-cell,
.location-cell,
.phone-cell {
  display: flex;
  align-items: center;
  font-weight: 500;
}

.guests-cell {
  display: flex;
  align-items: center;
}

.guests-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.status-cell {
  display: flex;
  align-items: center;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
}

.status-new {
  background: #dbeafe;
  color: #1e40af;
}

.status-progress {
  background: #fef3c7;
  color: #92400e;
}

.status-completed {
  background: #dcfce7;
  color: #166534;
}

/* Action buttons */
.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.action-button {
  border: none !important;
  border-radius: 0.5rem !important;
  padding: 0.5rem !important;
  transition: all 0.2s ease !important;
}

.edit-button {
  background: #3b82f6 !important;
  color: white !important;
}

.edit-button:hover {
  background: #2563eb !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3) !important;
}

.view-button {
  background: #10b981 !important;
  color: white !important;
}

.view-button:hover {
  background: #059669 !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 8px rgba(16, 185, 129, 0.3) !important;
}

/* Dialog styling */
.modern-dialog {
  border-radius: 1rem !important;
}

.edit-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
}

.form-input {
  width: 100%;
}

.cancel-button {
  background: #6b7280 !important;
  color: white !important;
  border: none !important;
}

.save-button {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  color: white !important;
  border: none !important;
}

.close-button {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  color: white !important;
  border: none !important;
}

/* Lead details styling */
.lead-details {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f1f5f9;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-weight: 600;
  color: #374151;
  min-width: 140px;
}

.detail-value {
  color: #6b7280;
  font-weight: 500;
}

/* Ensure proper shadow styling */
.shadow-1 {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}



/* Responsive improvements */
@media (max-width: 768px) {
  .flex.gap-6 {
    flex-direction: column;
  }
  
  .w-80 {
    width: 100%;
  }
  
  .filter-row {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .max-w-7xl {
    max-width: 100%;
  }
  
  .p-6 {
    padding: 1rem;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .action-button {
    padding: 0.375rem !important;
  }
}
</style> 